import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import "../styles/AuthStyles.css";
import { useAuth } from "../context/AuthContext";
import authBg from "../assets/auth-bg-v2.png";
import logo from "../assets/orvion-logo.png";
import CustomModal from "../components/CustomModal";

function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "freelancer", // default to freelancer
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: null
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate strong password: min 8 chars, 1 upper, 1 lower, 1 number, 1 special
    const pwd = formData.password || "";
    const strongPwdRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
    if (!strongPwdRegex.test(pwd)) {
      setPasswordError("Password must be at least 8 characters and include uppercase, lowercase, number and special character.");
      setLoading(false);
      return;
    }
    setPasswordError("");

    try {
      const data = await signup(formData.name, formData.email, formData.password, formData.role);

      if (data.user) {
        setModalState({
          isOpen: true,
          title: "Success",
          message: "Account created successfully!",
          type: "success",
          onConfirm: () => {
            setModalState(prev => ({ ...prev, isOpen: false }));
            navigate("/login");
          }
        });
      } else {
        setModalState({
          isOpen: true,
          title: "Signup Failed",
          message: data.msg || "Failed to create account",
          type: "error",
          onConfirm: () => setModalState(prev => ({ ...prev, isOpen: false }))
        });
      }
    } catch (err) {
      setModalState({
        isOpen: true,
        title: "Error",
        message: err.message,
        type: "error",
        onConfirm: () => setModalState(prev => ({ ...prev, isOpen: false }))
      });
    }

    setLoading(false);
  };

  return (
    <div className="orvion-auth-container">
      {/* Left Side - Visual */}
      <div className="orvion-auth-visual">
        <img src={authBg} alt="Background" className="orvion-auth-image-bg" />
        <div className="orvion-auth-overlay"></div>
        <Link to="/" className="orvion-back-link">
          ← Back to landing
        </Link>
      </div>

      {/* Right Side - Form Card */}
      <div className="orvion-auth-card">
        <div className="orvion-form-wrapper">
          <div className="orvion-logo-container">
            <img src={logo} alt="Orvion Logo" className="orvion-header-logo" />
          </div>

          <div className="orvion-auth-header">
            <h1 className="orvion-auth-title">Create an account</h1>
            <p className="orvion-auth-subtitle">Join Orvion Studio today</p>
          </div>

          {/* Google Button (Coming Soon) */}
          <div className="orvion-google-btn-container">
            <button className="orvion-google-btn" type="button" onClick={() => { }}>
              <FcGoogle size={22} className={window.innerWidth < 900 ? "" : ""} />
              {/* Hide Text in CSS on Hover, Icon remains */}
              <span className="google-btn-text">Create account with Google</span>
            </button>
          </div>

          <div className="orvion-divider">Or</div>

          <form onSubmit={handleSubmit} className="orvion-auth-form">
            <div className="orvion-form-group">
              <label>Full Name</label>
              <input
                type="text"
                name="name"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="orvion-form-group">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="orvion-form-group">
              <label>Password</label>
              <div className="orvion-password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Create your password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="orvion-password-toggle"
                  onClick={() => setShowPassword((s) => !s)}
                >
                  {showPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>
            {passwordError && <p className="orvion-field-error">{passwordError}</p>}

            <div className="orvion-role-selection">
              <div
                className={`orvion-role-toggle ${formData.role === "admin" ? "client-active" : ""}`}
                onClick={() => setFormData({ ...formData, role: formData.role === "freelancer" ? "admin" : "freelancer" })}
              >
                <div className={`orvion-role-slider`}></div>
                <div
                  className={`orvion-role-option ${formData.role === "freelancer" ? "active" : ""}`}
                  onClick={(e) => { e.stopPropagation(); setFormData({ ...formData, role: "freelancer" }); }}
                >
                  Partner
                </div>
                <div
                  className={`orvion-role-option ${formData.role === "admin" ? "active" : ""}`}
                  onClick={(e) => { e.stopPropagation(); setFormData({ ...formData, role: "admin" }); }}
                >
                  Client
                </div>
              </div>
            </div>

            <button type="submit" className="orvion-auth-btn" disabled={loading}>
              {loading ? <div className="loading-spinner"></div> : "Create an account"}
            </button>
          </form>

          <div className="orvion-auth-footer">
            Already have an account? <Link to="/login">Login</Link>
          </div>
        </div>
      </div>

      <CustomModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        onConfirm={modalState.onConfirm}
      />
    </div>
  );
}

export default Signup;
